import { getPanelPushSubscription, webPushSupported } from "./webPushClient";

const INSTALLATION_STORAGE_KEY = "webprofit:device-installation:v1";
let inMemoryInstallationId = "";

export const OWNER_PUSH_DEFAULT_PREFERENCES = Object.freeze({
  storefrontActivity: true,
  commercial: true,
  operations: true,
  erp: true,
  orders: true,
  payments: true,
  billing: true,
  security: true,
  panelActivity: false,
  system: true,
  campaigns: false,
});

export function normalizeOwnerPushPreferences(value = {}) {
  return Object.fromEntries(
    Object.entries(OWNER_PUSH_DEFAULT_PREFERENCES).map(([key, fallback]) => [
      key,
      key === "campaigns"
        ? false
        : Object.prototype.hasOwnProperty.call(value || {}, key)
          ? value[key] !== false
          : fallback,
    ]),
  );
}

function randomInstallationId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `wp_${crypto.randomUUID().replace(/-/g, "")}`;
  }
  const seed = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;
  return `wp_${seed.replace(/[^a-z0-9_]/gi, "").slice(0, 100)}`;
}

export function getPanelInstallationId() {
  if (typeof window === "undefined") return "";
  if (/^[A-Za-z0-9_-]{16,120}$/.test(inMemoryInstallationId)) return inMemoryInstallationId;
  try {
    const existing = String(window.localStorage.getItem(INSTALLATION_STORAGE_KEY) || "").trim();
    if (/^[A-Za-z0-9_-]{16,120}$/.test(existing)) {
      inMemoryInstallationId = existing;
      return existing;
    }
    const created = randomInstallationId();
    inMemoryInstallationId = created;
    window.localStorage.setItem(INSTALLATION_STORAGE_KEY, created);
    return created;
  } catch {
    inMemoryInstallationId = inMemoryInstallationId || randomInstallationId();
    return inMemoryInstallationId;
  }
}

export function currentNotificationPermission() {
  if (!webPushSupported()) return "unsupported";
  return ["default", "granted", "denied"].includes(Notification.permission)
    ? Notification.permission
    : "default";
}

export async function resolveProjectPushState(status = {}) {
  const supported = webPushSupported();
  const permission = currentNotificationPermission();
  let browserSubscription = null;
  let subscriptionError = null;

  if (supported) {
    try {
      browserSubscription = await getPanelPushSubscription();
    } catch (error) {
      subscriptionError = error;
    }
  }

  const endpoint = browserSubscription?.endpoint || "";
  const projectSubscription = endpoint
    ? (status?.ownSubscriptions || []).find((item) => item?.endpoint === endpoint) || null
    : null;
  const projectSubscriptionMatched = Boolean(projectSubscription);
  const activeForCurrentProject = Boolean(
    supported &&
    permission === "granted" &&
    browserSubscription &&
    projectSubscriptionMatched,
  );
  const blocked = permission === "denied";
  const eligibleToActivate = Boolean(
    status?.enabled === true &&
    status?.configured === true &&
    status?.memberActive !== false &&
    supported &&
    !blocked &&
    !activeForCurrentProject,
  );

  return {
    supported,
    permission,
    browserSubscription,
    projectSubscription,
    projectSubscriptionMatched,
    activeForCurrentProject,
    blocked,
    eligibleToActivate,
    subscriptionError,
  };
}

export function adoptionCadenceAllows(adoption) {
  const until = adoption?.snoozedUntil ? new Date(adoption.snoozedUntil).getTime() : 0;
  return !Number.isFinite(until) || until <= Date.now();
}
